import { Fragment, useEffect, useRef } from 'react';
import { Icon, Spinner } from '~/components/atomic';
import { IconButtonWrapper } from '~/components/common';
import { MessageResponse } from '~/types';
import {
  disableScrollLock,
  enableScrollLock,
  getMemberName,
  parseTextWithItalics,
  validateEmail,
} from '~/utils';
import AIMark from '~/assets/img/icons/chat_ai_mark.svg';
import { useCommonChatStore, useUserStore } from '~/store';
import * as S from './ChatHistoryContent.styled';

interface HistoryModalProps {
  messages: MessageResponse[];
  isOpen: boolean | null;
  onClose: () => void;
}

export default function ConceptChatHistoryContent({
  messages,
  isOpen,
  onClose,
}: HistoryModalProps) {
  const email = useUserStore((state) => state.email);
  const members = useCommonChatStore((state) => state.chatMembers);
  const messageListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (isOpen) {
      enableScrollLock();
      scrollToBottom();
    } else {
      disableScrollLock();
    }
    return () => {
      disableScrollLock();
    };
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  };

  const renderTextSegments = (text: string) => {
    const segments = parseTextWithItalics(text);
    return segments.map(({ italic, text }, index) =>
      text === '\n' ? (
        <br key={index} />
      ) : (
        <p className={italic ? 'italic' : ''} key={index}>
          {text}
        </p>
      ),
    );
  };

  return (
    <>
      {isOpen && (
        <S.Container>
          <S.Top>
            <IconButtonWrapper onClick={onClose}>
              <Icon size={24} iconName={'Close'} />
            </IconButtonWrapper>
          </S.Top>
          <S.MessageList ref={messageListRef}>
            {messages.map(
              ({ msg, id: responseEmail, send_time, name, isPending }, idx) => {
                const userMsg = responseEmail === email;
                const aiMsg = !validateEmail(responseEmail);

                if (responseEmail === 'system_message') {
                  const [name, ...rest] = msg.split(' ');
                  const restMessage = rest.join(' ');

                  return (
                    <S.SystemMessageBox key={idx}>
                      <S.ChatContentWrapper className='info'>
                        <S.SystemMessage>
                          <span>{name}</span>
                          {restMessage}
                        </S.SystemMessage>
                      </S.ChatContentWrapper>
                    </S.SystemMessageBox>
                  );
                }

                if (send_time === 'error') {
                  return (
                    <S.SystemMessageBox key={idx}>
                      <S.ChatContentWrapper className={'other'}>
                        <S.Content className='content'>
                          <p>{msg}</p>
                        </S.Content>
                      </S.ChatContentWrapper>
                    </S.SystemMessageBox>
                  );
                }

                if (!send_time) return null;
                const isLastOrDifferentUser =
                  idx === messages.length - 1 ||
                  messages[idx + 1].id !== responseEmail;

                return (
                  <Fragment key={idx}>
                    <S.ChatList isGrouped={false}>
                      {!userMsg && (
                        <S.ChatNameBox>
                          {getMemberName(members, responseEmail) && aiMsg && (
                            <img src={AIMark} alt='group_icon' />
                          )}
                          <S.ChatName className='ai_name'>
                            {!userMsg &&
                              (name ||
                                getMemberName(members, responseEmail) || (
                                  <span>Deleted character</span>
                                ))}
                          </S.ChatName>
                        </S.ChatNameBox>
                      )}

                      <S.ChatContentWrapper
                        className={userMsg ? 'user' : 'other'}
                      >
                        <S.Content className={`content`}>
                          {renderTextSegments(msg)}
                        </S.Content>
                        {isLastOrDifferentUser && isPending && (
                          <S.ChatTime>
                            <Spinner size='small' color='content_light_5' />
                          </S.ChatTime>
                        )}
                      </S.ChatContentWrapper>
                    </S.ChatList>
                  </Fragment>
                );
              },
            )}
          </S.MessageList>
        </S.Container>
      )}
    </>
  );
}
